import React from "react";
import Man1 from "../assets/img/man1.webp";
import Man2 from "../assets/img/man2.jpg";
import Man3 from "../assets/img/man3.jpg";
import Woman1 from "../assets/img/woman2.jpg";
import Man4 from "../assets/img/man4.jpg";
import Woman3 from "../assets/img/woman6.jpg";

/**
 * ThirdHome.js
 * "Our Team" page component with dynamic theme support.
 */

const team = [
    {
        id: 1,
        name: "Ethan Huxley",
        role: "Product Manager",
        bio: "Leads product strategy and coordinates seamlessly between design and engineering teams.",
        initials: "EH",
        image: Man1
    },
    {
        id: 2,
        name: "Jacob Williams",
        role: "Crypto Trading Specialist",
        bio: "Analyzes digital asset markets and provides algorithmic trading strategy insights.",
        initials: "JW",
        image: Man2
    },
    {
        id: 3,
        name: "Sofia Chen",
        role: "Blockchain Security Engineer",
        bio: "Audits smart contracts and maintains zero-trust infrastructure protocols.",
        initials: "SC",
        image: Woman1
    },
    {
        id: 4,
        name: "Noah Smith",
        role: "Client Support Agent",
        bio: "Delivers 24/7 dedicated assistance to ensure seamless onboarding and account care.",
        initials: "NS",
        image: Man3
    },
    {
        id: 5,
        name: "Ammy Gray",
        role: "Crypto Trading Specialist",
        bio: "Specializes in market risk management and liquidity optimization for active traders.",
        initials: "AG",
        image: Woman3
    },
    {
        id: 6,
        name: "Kevin Miller",
        role: "Crypto Trading Specialist",
        bio: "Focuses on institutional asset flows and automated execution mechanisms.",
        initials: "KM",
        image: Man4
    },
];

export default function ThirdHome({ theme = "green", primaryColor }) {
    // Dynamic theme color selection (defaulting to green #16a34a)
    const primary = primaryColor || (theme === "green" ? "#16a34a" : "#3b82f6");

    return (
        <div style={styles.wrapper}>
            <div style={styles.container}>
                {/* Header */}
                <header style={styles.header}>
                    <h1 style={{ ...styles.title, color: primary }}>Our Team</h1>
                    <p style={styles.subtitle}>
                        Small team, big impact. Meet the people building products and supporting customers.
                    </p>
                </header>

                {/* Team Grid */}
                <main>
                    <section style={styles.grid} aria-label="Team Members">
                        {team.map((member) => (
                            <article key={member.id} style={styles.card}>
                                {/* Member Avatar */}
                                <div style={styles.avatarContainer}>
                                    <img
                                        src={member.image}
                                        alt={member.name}
                                        style={styles.avatarImg}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    {/* Fallback Initials Avatar */}
                                    <div style={{ ...styles.avatarFallback, backgroundColor: `${primary}25`, color: primary }}>
                                        {member.initials}
                                    </div>
                                </div>

                                {/* Content Details */}
                                <div style={styles.details}>
                                    <div style={styles.cardHeader}>
                                        <div>
                                            <h3 style={styles.memberName}>{member.name}</h3>
                                            <div style={styles.memberRole}>{member.role}</div>
                                        </div>

                                        <button
                                            aria-label={`Message ${member.name}`}
                                            style={{
                                                ...styles.messageBtn,
                                                borderColor: primary,
                                                color: primary,
                                            }}
                                        >
                                            Message
                                        </button>
                                    </div>

                                    <p style={styles.bio}>{member.bio}</p>

                                    {/* Social Links */}
                                    <div style={styles.socialGroup}>
                                        <SocialIcon type="twitter" color={primary} />
                                        <SocialIcon type="linkedin" color={primary} />
                                        <SocialIcon type="github" color={primary} />
                                    </div>
                                </div>
                            </article>
                        ))}
                    </section>

                    {/* Footer CTA */}
                    <div style={styles.ctaWrapper}>
                        <button style={{ ...styles.ctaBtn, backgroundColor: primary }}>
                            See full team
                        </button>
                    </div>
                </main>
            </div>
        </div>
    );
}

// Inline Social Icon SVG Component
function SocialIcon({ type, color }) {
    const icons = {
        twitter: (
            <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
        ),
        linkedin: (
            <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2zM4 6a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
        ),
        github: (
            <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
        )
    };

    return (
        <a
            href={`#${type}`}
            aria-label={type}
            style={{ ...styles.socialIcon, backgroundColor: `${color}18`, color: color }}
        >
            <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                {icons[type]}
            </svg>
        </a>
    );
}

const styles = {
    wrapper: {
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "transparent",
        color: "#f8fafc",
        fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
        display: "flex",
        justifyContent: "center",
        padding: "40px 0",
    },
    container: {
        width: "100%",
        maxWidth: 1140,
        padding: "0 24px",
        boxSizing: "border-box",
    },
    header: {
        maxWidth: 720,
        margin: "0 auto 40px",
        textAlign: "center",
        backgroundColor: "transparent",
    },
    title: {
        fontSize: "clamp(2rem, 4vw, 2.75rem)",
        fontWeight: 800,
        margin: "0 0 12px 0",
        letterSpacing: "-0.02em",
    },
    subtitle: {
        fontSize: 16,
        color: "#94a3b8",
        margin: 0,
        lineHeight: 1.5,
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: 24,
    },
    card: {
        backgroundColor: "rgba(26, 32, 40, 0.85)",
        backdropFilter: "blur(12px)",
        borderRadius: 16,
        padding: 20,
        border: "1px solid rgba(255, 255, 255, 0.06)",
        boxShadow: "0 10px 25px -10px rgba(0, 0, 0, 0.3)",
        display: "flex",
        gap: 16,
        alignItems: "flex-start",
    },
    avatarContainer: {
        position: "relative",
        width: 88,
        height: 88,
        flexShrink: 0,
        borderRadius: 12,
        overflow: "hidden",
        backgroundColor: "#111827",
    },
    avatarImg: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: 12,
    },
    avatarFallback: {
        display: "none",
        position: "absolute",
        inset: 0,
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 20,
        borderRadius: 12,
    },
    details: {
        flex: 1,
        minWidth: 0,
    },
    cardHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
        marginBottom: 8,
    },
    memberName: {
        margin: 0,
        fontSize: 17,
        fontWeight: 700,
        color: "#f1f5f9",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    memberRole: {
        marginTop: 2,
        fontSize: 13,
        color: "#94a3b8",
        fontWeight: 500,
    },
    messageBtn: {
        backgroundColor: "transparent",
        border: "1px solid",
        padding: "6px 12px",
        borderRadius: 8,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: "nowrap",
        transition: "opacity 0.2s ease",
    },
    bio: {
        margin: "0 0 12px 0",
        fontSize: 14,
        color: "#cbd5e1",
        lineHeight: 1.5,
    },
    socialGroup: {
        display: "flex",
        gap: 8,
    },
    socialIcon: {
        width: 32,
        height: 32,
        borderRadius: 8,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        textDecoration: "none",
        transition: "transform 0.15s ease",
    },
    ctaWrapper: {
        textAlign: "center",
        marginTop: 40,
    },
    ctaBtn: {
        color: "#ffffff",
        border: "none",
        padding: "12px 28px",
        borderRadius: 10,
        fontSize: 15,
        fontWeight: 600,
        cursor: "pointer",
        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
        transition: "transform 0.2s ease",
    },
};